/**
 * Admin Dashboard Page
 * 
 * Main admin panel with system stats, user management, and monitoring.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Activity, BarChart3, Shield, 
  TrendingUp, Clock, DollarSign, UserCheck,
  AlertTriangle, RefreshCw
} from 'lucide-react';
import { AppShell, GlassPanel } from '@/components/layout';
import { Button, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { Navigate, Link } from 'react-router-dom';

interface SystemHealth {
  status: string;
  database: string;
  redis: string;
  active_users: number;
  active_sessions: number;
  uptime_seconds: number;
  version: string;
}

interface AdminStats {
  total_users: number;
  active_users: number;
  new_users_today: number;
  new_users_this_week: number;
  total_sessions_today: number;
  total_sessions_this_week: number;
  users_by_tier: Record<string, number>;
  revenue_estimate_mtd: number;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  color?: string;
}> = ({ title, value, icon, trend, color = 'ocean-cyan' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-navy-800/50 backdrop-blur-sm border border-white/5 rounded-xl p-6"
  >
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-lg bg-${color}/20`}>
        {icon}
      </div>
      {trend && (
        <span className="text-sm text-green-400 flex items-center gap-1">
          <TrendingUp size={14} />
          {trend}
        </span>
      )}
    </div>
    <h3 className="text-gray-400 text-sm mb-1">{title}</h3>
    <p className="text-2xl font-display font-bold text-white">{value}</p>
  </motion.div>
);

const HealthIndicator: React.FC<{ status: string; label: string }> = ({ status, label }) => {
  const colors = {
    healthy: 'bg-green-500',
    degraded: 'bg-yellow-500',
    unhealthy: 'bg-red-500',
    unavailable: 'bg-gray-500',
  };
  
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${colors[status as keyof typeof colors] || colors.unavailable}`} />
      <span className="text-gray-300 text-sm">{label}</span>
      <span className="text-gray-500 text-xs capitalize">({status})</span>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect non-admins
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [healthRes, statsRes] = await Promise.all([
        api.get('/admin/system/health'),
        api.get('/admin/system/stats'),
      ]);
      
      setHealth(healthRes.data);
      setStats(statsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !health && !stats) {
    return (
      <AppShell>
        <div className="p-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <div className="grid grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="p-8 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">System monitoring and user management</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </Button>
            
            <Link to="/admin/users">
              <Button variant="primary" size="sm">
                <Users size={16} />
                Manage Users
              </Button>
            </Link>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {/* System Health */}
        <GlassPanel className="mb-8 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="text-ocean-cyan" />
            System Health
          </h2>
          
          <div className="flex items-center gap-8">
            <HealthIndicator 
              status={health?.status || 'unknown'} 
              label="Overall" 
            />
            <HealthIndicator 
              status={health?.database || 'unknown'} 
              label="Database" 
            />
            <HealthIndicator 
              status={health?.redis || 'unknown'} 
              label="Redis" 
            />
            
            <div className="ml-auto flex items-center gap-6 text-sm text-gray-400">
              <span>Version: <strong className="text-white">{health?.version}</strong></span>
              <span>Active Sessions: <strong className="text-ocean-cyan">{health?.active_sessions}</strong></span>
            </div>
          </div>
        </GlassPanel>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={stats?.total_users || 0}
            icon={<Users className="text-ocean-cyan" size={24} />}
          />
          <StatCard
            title="Active Users (24h)"
            value={stats?.active_users || 0}
            icon={<UserCheck className="text-green-400" size={24} />}
            color="green-400"
          />
          <StatCard
            title="Sessions Today"
            value={stats?.total_sessions_today || 0}
            icon={<Activity className="text-blue-400" size={24} />}
            color="blue-400"
          />
          <StatCard
            title="Revenue (MTD)"
            value={`$${stats?.revenue_estimate_mtd?.toFixed(2) || '0.00'}`}
            icon={<DollarSign className="text-yellow-400" size={24} />}
            color="yellow-400"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          {/* Users by Tier */}
          <GlassPanel className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="text-ocean-cyan" />
              Users by Tier
            </h3>
            
            <div className="space-y-3">
              {['basic', 'standard', 'pro', 'elite'].map((tier) => {
                const count = stats?.users_by_tier?.[tier] || 0;
                const total = stats?.total_users || 1;
                const percentage = Math.round((count / total) * 100);
                
                return (
                  <div key={tier}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-300 capitalize">{tier}</span>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                    <div className="h-2 bg-navy-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="h-full bg-gradient-to-r from-ocean-cyan to-blue-500 rounded-full"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassPanel>

          {/* Growth Stats */}
          <GlassPanel className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="text-ocean-cyan" />
              Growth
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-navy-700/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">New Users Today</p>
                <p className="text-2xl font-bold text-green-400">
                  +{stats?.new_users_today || 0}
                </p>
              </div>
              <div className="bg-navy-700/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">New Users This Week</p>
                <p className="text-2xl font-bold text-blue-400">
                  +{stats?.new_users_this_week || 0}
                </p>
              </div>
              <div className="bg-navy-700/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">Sessions Today</p>
                <p className="text-2xl font-bold text-ocean-cyan">
                  {stats?.total_sessions_today || 0}
                </p>
              </div>
              <div className="bg-navy-700/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">Sessions This Week</p>
                <p className="text-2xl font-bold text-purple-400">
                  {stats?.total_sessions_this_week || 0}
                </p>
              </div>
            </div>
          </GlassPanel>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-3 gap-6">
          <Link to="/admin/users">
            <GlassPanel className="p-6 hover:border-ocean-cyan/50 transition-colors cursor-pointer">
              <Users className="text-ocean-cyan mb-3" size={32} />
              <h3 className="text-lg font-semibold text-white">User Management</h3>
              <p className="text-gray-400 text-sm">View, edit, and manage user accounts</p>
            </GlassPanel>
          </Link>
          
          <Link to="/admin/analytics">
            <GlassPanel className="p-6 hover:border-ocean-cyan/50 transition-colors cursor-pointer">
              <BarChart3 className="text-blue-400 mb-3" size={32} />
              <h3 className="text-lg font-semibold text-white">Analytics</h3>
              <p className="text-gray-400 text-sm">Usage statistics and cost tracking</p>
            </GlassPanel>
          </Link>
          
          <Link to="/admin/audit">
            <GlassPanel className="p-6 hover:border-ocean-cyan/50 transition-colors cursor-pointer">
              <Shield className="text-purple-400 mb-3" size={32} />
              <h3 className="text-lg font-semibold text-white">Audit Logs</h3>
              <p className="text-gray-400 text-sm">Security events and activity history</p>
            </GlassPanel>
          </Link>
        </div>
      </div>
    </AppShell>
  );
};

export default AdminDashboard;

